import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, db } from '@/lib/firebase';
import { isSignInWithEmailLink, signInWithEmailLink, updatePassword } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import type { UserStatus } from '@/types';

export function CompleteInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'setPassword' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    const inviteId = searchParams.get('inviteId');
    if (!inviteId) {
      setStatus('error');
      setMessage('Enlace inválido: falta el identificador de invitación.');
      return;
    }

    const run = async () => {
      try {
        const inviteSnap = await getDoc(doc(db, 'userInvites', inviteId));
        if (!inviteSnap.exists()) {
          setStatus('error');
          setMessage('Invitación no encontrada o ya utilizada.');
          return;
        }
        const invite = inviteSnap.data();
        if (invite.usedAt) {
          setStatus('error');
          setMessage('Esta invitación ya fue utilizada.');
          return;
        }
        const email = (invite.email as string)?.trim();
        if (!email) {
          setStatus('error');
          setMessage('Invitación inválida.');
          return;
        }

        const fullUrl = window.location.href;
        if (!isSignInWithEmailLink(auth, fullUrl)) {
          setStatus('error');
          setMessage('Este enlace no es válido para establecer contraseña. Asegúrate de usar el enlace del correo.');
          return;
        }

        const userCred = await signInWithEmailLink(auth, email, fullUrl);
        const uid = userCred.user.uid;
        const userRef = doc(db, 'users', uid);
        const userSnap = await getDoc(userRef);

        const payload = {
          email,
          displayName: userCred.user.displayName || email,
          companyId: invite.companyId as string,
          role: invite.role as string,
          status: (invite.status as UserStatus) ?? 'active',
          updatedAt: serverTimestamp(),
        };

        if (userSnap.exists()) {
          await updateDoc(userRef, payload);
        } else {
          await setDoc(userRef, {
            ...payload,
            createdAt: serverTimestamp(),
          });
        }

        await updateDoc(doc(db, 'userInvites', inviteId), {
          usedAt: serverTimestamp(),
          usedByUid: uid,
        });

        setStatus('setPassword');
        setMessage('');
      } catch (err) {
        console.error('Error completando invitación', err);
        setStatus('error');
        const errMsg = err instanceof Error ? err.message : String(err);
        if (errMsg.includes('invalid-action-code') || errMsg.includes('auth/invalid-action-code')) {
          setMessage(
            'Este enlace ya fue utilizado o ha caducado. Los enlaces de invitación solo sirven una vez y expiran en unas horas. Si ya completaste la invitación antes, inicia sesión con tu correo y contraseña. Si no, pide al administrador que te envíe una nueva invitación.'
          );
        } else {
          setMessage(errMsg);
        }
      }
    };

    run();
  }, [searchParams, navigate]);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    if (password.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden.');
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      setPasswordError('Sesión perdida. Ve a iniciar sesión y usa "¿Olvidaste tu contraseña?" si hace falta.');
      return;
    }
    setSavingPassword(true);
    try {
      await updatePassword(user, password);
      setStatus('success');
      setMessage('Contraseña guardada. Redirigiendo al panel...');
      setTimeout(() => navigate('/app/dashboard', { replace: true }), 1200);
    } catch (err) {
      console.error('Error estableciendo contraseña', err);
      setPasswordError(err instanceof Error ? err.message : 'Error al guardar la contraseña.');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>PsyTalent · Invitación</CardTitle>
        </CardHeader>
        <CardContent>
          {status === 'loading' && <p className="text-muted-foreground">Completando invitación...</p>}
          {status === 'setPassword' && (
            <form onSubmit={handleSetPassword} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Cuenta vinculada correctamente. Establece una contraseña para poder iniciar sesión más adelante (correo + contraseña).
              </p>
              {passwordError && (
                <p className="text-sm text-destructive">{passwordError}</p>
              )}
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  minLength={6}
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Repetir contraseña</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite la contraseña"
                  minLength={6}
                  autoComplete="new-password"
                  required
                />
              </div>
              <Button type="submit" disabled={savingPassword} className="w-full">
                {savingPassword ? 'Guardando...' : 'Guardar contraseña e ir al panel'}
              </Button>
            </form>
          )}
          {status === 'success' && (
            <p className="text-green-700">{message}</p>
          )}
          {status === 'error' && (
            <div className="space-y-4">
              <p className="text-destructive">{message}</p>
              <Button variant="outline" onClick={() => navigate('/login', { replace: true })}>
                Ir a iniciar sesión
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
